import { PageSection, Title } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useState, useEffect } from 'react'

import { PatientProgramList } from '@app/components'
import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import {
  VisitListComponent,
  VisitListVariant,
} from '@app/components/visit/list'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import { useProgramEnrolments } from '@app/domain/patient/patient.hooks'
import type { IPatientId } from '@app/domain/patient/patient.interface'
import { useListVisits } from '@app/domain/visit/visit.hooks'
import type { ISortDirection } from '@app/lib/sorting'

export interface Props {
  patientId: IPatientId
}

export const VisitsAndProgramsTabContent = ({ patientId }: Props) => {
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(50)
  const [location, setLocation] = useState<string>(null)
  const [status, setStatus] = useState<string[]>([])
  const [sortField, setSortField] = useState<string>(null)
  const [sortDirection, setSortDirection] = useState<ISortDirection>(null)

  const { visits, totalNumberOfRecords, getVisits } = useListVisits()
  const {
    patientProgramEnrolments,
    getPatientProgramEnrolments,
    sendInteractionToIntakeAnywhere,
    revokeInteraction,
  } = useProgramEnrolments()

  const onRefresh = useCallback(() => {
    getVisits(
      pageNumber,
      recordsPerPage,
      location,
      null,
      null,
      status,
      null,
      patientId,
      sortDirection,
      sortField,
    )
    getPatientProgramEnrolments(patientId)
  }, [
    getVisits,
    getPatientProgramEnrolments,
    patientId,
    pageNumber,
    recordsPerPage,
    location,
    status,
    sortField,
    sortDirection,
  ])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const onSetPage = useCallback((updatedPageNumber: number) => {
    setPageNumber(updatedPageNumber)
  }, [])

  const onPerPageSelect = useCallback((updatedRecordsPerPage: number) => {
    setPageNumber(1)
    setRecordsPerPage(updatedRecordsPerPage)
  }, [])

  const onSetLocation = useCallback((newLocation) => {
    setPageNumber(1)
    setLocation(newLocation)
  }, [])

  const onSetStatus = useCallback((newStatus) => {
    setPageNumber(1)
    setStatus(newStatus)
  }, [])

  const onSetSort = useCallback(
    (newField: string, newDirection: ISortDirection) => {
      setPageNumber(1)
      setSortDirection(newDirection)
      setSortField(newField)
    },
    [],
  )

  const handleSendInteractionToIntakeAnywhere = useCallback(
    (programInteractionId: string) => {
      sendInteractionToIntakeAnywhere(patientId, programInteractionId)
    },
    [patientId, sendInteractionToIntakeAnywhere],
  )

  const handleRevokeInteraction = useCallback(
    (programInteractionId: string) => {
      revokeInteraction(patientId, programInteractionId)
    },
    [patientId, revokeInteraction],
  )

  return (
    <>
      {visits ? (
        <PageSection>
          <Title headingLevel="h2" size="xl">
            Visits
          </Title>
          <VisitListComponent
            filters={{
              location,
              onLocationUpdated: onSetLocation,
              status,
              onStatusUpdated: onSetStatus,
            }}
            pagination={{
              onSetPage,
              onPerPageSelect,
              pageNumber,
              recordsPerPage,
              totalNumberOfRecords,
            }}
            onRefresh={onRefresh}
            options={{
              variant: VisitListVariant.HidePatient,
            }}
            sorting={{
              onSetSort,
              sortField,
              sortDirection,
            }}
            visits={visits}
          />
        </PageSection>
      ) : (
        <PageSection>No Visits</PageSection>
      )}
      <RequireFeatureFlag
        key="program_configuration_enabled"
        feature={FeatureFlagId.ProgramConfiguration}
      >
        {patientProgramEnrolments ? (
          <PageSection>
            <Title headingLevel="h2" size="xl">
              Program Enrolments
            </Title>
            <PatientProgramList
              patientProgramEnrolments={patientProgramEnrolments}
              onSendInteractionToIntakeAnywhere={
                handleSendInteractionToIntakeAnywhere
              }
              onRevokeInteraction={handleRevokeInteraction}
            />
          </PageSection>
        ) : (
          <PageSection>No Program Enrolments</PageSection>
        )}
      </RequireFeatureFlag>
    </>
  )
}
